import React, { useEffect, useState } from 'react';
import { Checkbox, Form, Input, Modal } from 'antd';

export const NAME_MAX = 100;

/**
 * Save a combined list, or rename one (KPR-1022). Saving names it (the card's
 * name to start) and pins it to Home unless the person says not to (Zach,
 * 2026-10-20: "Save and pin, pin on"). Renaming asks the name alone.
 *
 *   <CombinedListNameModal mode="save" | "rename" open initialName onSubmit({ name, pin }) onCancel error />
 *
 * `onSubmit` may throw: the dialog stays open and shows `error` (the page's
 * words for the API's refusal).
 */
export default function CombinedListNameModal({ open, mode = 'save', initialName = '', onSubmit, onCancel, error = null }) {
	const [name, setName] = useState(initialName);
	const [pin, setPin] = useState(true);
	const [busy, setBusy] = useState(false);
	const [touched, setTouched] = useState(false);

	useEffect(() => {
		if (!open) return;
		setName(initialName);
		setPin(true);
		setTouched(false);
	}, [open, initialName]);

	const trimmed = name.trim();
	const invalid = !trimmed ? 'Enter a name' : trimmed.length > NAME_MAX ? `Keep it under ${NAME_MAX} characters` : null;
	const saving = mode === 'save';

	const submit = async () => {
		setTouched(true);
		if (invalid) return;
		setBusy(true);
		try {
			await onSubmit({ name: trimmed, pin: saving ? pin : undefined });
		} catch {
			// The page shows what went wrong (`error`); the dialog stays.
		} finally {
			setBusy(false);
		}
	};

	return (
		<Modal
			open={open}
			title={saving ? 'Save this list' : 'Rename list'}
			okText={saving ? 'Save list' : 'Rename'}
			onOk={submit}
			onCancel={onCancel}
			confirmLoading={busy}
			destroyOnClose
		>
			<Form layout="vertical" onFinish={submit}>
				<Form.Item
					label="Name"
					validateStatus={touched && invalid ? 'error' : error ? 'error' : undefined}
					help={touched && invalid ? invalid : error || undefined}
				>
					<Input
						value={name}
						onChange={event => setName(event.target.value)}
						maxLength={NAME_MAX + 20}
						autoFocus
						aria-label="Name"
						data-testid="combined-list-name"
					/>
				</Form.Item>
				{saving ? (
					<>
						<Checkbox checked={pin} onChange={event => setPin(event.target.checked)} data-testid="combined-list-pin">
							Pin to Home
						</Checkbox>
						<p className="combined-list__save-note">It keeps the filter and the sort you have now. A collection you follow or are given later joins it by itself when it holds this card.</p>
					</>
				) : null}
			</Form>
		</Modal>
	);
}
